/**
 * Options Analyst page — a pure navigation/composition wrapper that hosts
 * the three existing Option Indicator pages (Option Indicator, OI Flow
 * Charts, Strike Charts) as tabs under a single sidebar entry, replacing
 * their three previous standalone routes/nav leaves.
 *
 * This page renders `OptionIndicatorPage`/`OIChartsPage`/`OIStrikeChartsPage`
 * completely unmodified as children — no state, calculations, or API calls
 * from any of the three are touched. The only cross-cutting change made
 * elsewhere for this to nest cleanly is a one-line fix in `OIChartsPage.tsx`
 * so its own `setSearchParams` call merges into the existing query string
 * (preserving this page's `?tab=`) instead of overwriting it wholesale —
 * `OptionIndicatorPage.tsx` already did this correctly, and
 * `OIStrikeChartsPage.tsx` doesn't touch the URL at all.
 *
 * Tab bar mechanics (mount-once-keep-alive via `TabPanel`, `?tab=` synced
 * to the URL with `replace` so switching tabs doesn't spam history) mirror
 * `OptionsDashboardPage.tsx`'s own established pattern for visual and
 * behavioral consistency across the app.
 *
 * Symbol state (frontend symbol-state-sync fix, Option Analyst audit
 * Section 8/10): Option Indicator, OI Flow Charts, Strike Charts and Dual
 * View each render NIFTY and BANKNIFTY side by side unconditionally (no
 * single-symbol selector exists on any of them — this page's own module
 * docstring previously claimed otherwise; that claim was stale and has
 * been corrected) — the audit's guidance for these is "leave alone, this
 * dual/comparative view is intentional." AI Insights and Strategy Builder
 * are the only tabs with an actual single-INDEX-symbol concept; they
 * already share one `symbol` value (`useState` below). The one real gap
 * the audit found: that shared value wasn't persisted anywhere, so it
 * silently reset to NIFTY on every remount and couldn't be
 * deep-linked/bookmarked — unlike `tab`, which already round-trips through
 * `?tab=`. Fixed below by mirroring `symbol` into `?symbol=` with the same
 * merge+replace pattern already used for `tab`, so a reload or shared link
 * restores the index the user was actually looking at.
 *
 * F&O Trade Analysis (added later) is a THIRD, separate single-symbol tab
 * — but scoped to individual F&O-enabled EQUITIES (RELIANCE, TCS, ...),
 * resolved against `StockMaster`, not the `symbol`/`SYMBOL_OPTIONS`
 * NIFTY/BANKNIFTY `Instrument` toggle above. It deliberately does NOT
 * share `symbol` (an early version wrongly did, producing "Stock 'NIFTY'
 * not found" since NIFTY has no `StockMaster` row) — it gets its own
 * `fnoSymbol` state + `?fnoSymbol=` param and a free-text stock search
 * (`StockSymbolAutocomplete`) instead of the index toggle.
 */
import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Paper, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { AnalystIndexViewProvider, type AnalystIndexView } from "@/context/AnalystIndexViewContext";
import OptionIndicatorPage from "@/pages/OptionIndicatorPage";
import OIChartsPage from "@/pages/OIChartsPage";
import OIStrikeChartsPage from "@/pages/OIStrikeChartsPage";
import DualSymbolDashboardPage from "@/pages/DualSymbolDashboardPage";
import StrategyAdjustmentPage from "@/pages/StrategyAdjustmentPage";
import { FnoTradeAnalysisPanel, StrategyInsightsPanel, StrategyRecommendationSection } from "@/pages/options-dashboard";
import HelpGuideLink from "@/components/HelpGuideLink";
import StockSymbolAutocomplete from "@/components/StockSymbolAutocomplete";

// Index view segmented control ("Both | NIFTY | BANKNIFTY"). Applies only to
// the four tabs that render both indices side by side; the provider below
// just tells those pages which section(s) to HIDE -- nothing is unmounted or
// refetched, so switching views keeps every tab's data and state intact.
const INDEX_VIEW_OPTIONS: { value: AnalystIndexView; label: string }[] = [
  { value: "both", label: "Both" },
  { value: "NIFTY", label: "NIFTY" },
  { value: "BANKNIFTY", label: "BANKNIFTY" },
];
const DUAL_INDEX_TABS: AnalystTab[] = ["indicator", "charts", "strike-charts", "dual"];

type AnalystTab =
  | "indicator"
  | "charts"
  | "strike-charts"
  | "dual"
  | "ai-insights"
  | "strategy-builder"
  | "fno-trade-analysis";

const ANALYST_TABS: { value: AnalystTab; label: string }[] = [
  { value: "indicator", label: "Option Indicator" },
  { value: "charts", label: "OI Flow Charts" },
  { value: "strike-charts", label: "Strike Charts" },
  { value: "dual", label: "Dual View (BN/NF)" },
  { value: "ai-insights", label: "AI Insights" },
  { value: "strategy-builder", label: "Strategy Builder" },
  { value: "fno-trade-analysis", label: "F&O Trade Analysis" },
];

// Symbol used only by the AI Insights / Strategy Builder tabs below -- the
// other four tabs each render both indices side by side unconditionally
// and have no single-symbol state to sync (see module docstring), so this
// stays scoped to just these two tabs rather than lifted across all six.
const SYMBOL_OPTIONS = ["NIFTY", "BANKNIFTY"] as const;
type AnalystSymbol = (typeof SYMBOL_OPTIONS)[number];

function isAnalystSymbol(value: string | null): value is AnalystSymbol {
  return value === "NIFTY" || value === "BANKNIFTY";
}

/**
 * Keeps a tab's content mounted once visited (rather than tearing it down
 * every time it's hidden) purely via CSS — same rationale and pattern as
 * `OptionsDashboardPage.tsx`'s own `TabPanel`.
 */
function TabPanel({ active, children }: { active: boolean; children: ReactNode }) {
  return <Box sx={{ display: active ? "block" : "none" }}>{children}</Box>;
}

export function OptionsAnalystPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<AnalystTab>(() => {
    const fromUrl = searchParams.get("tab");
    return fromUrl === "charts" ||
      fromUrl === "strike-charts" ||
      fromUrl === "dual" ||
      fromUrl === "ai-insights" ||
      fromUrl === "strategy-builder" ||
      fromUrl === "fno-trade-analysis"
      ? fromUrl
      : "indicator";
  });
  // Shared symbol for the AI Insights / Strategy Builder tabs only. Seeded
  // from `?symbol=` (falls back to NIFTY for an absent/invalid value) so a
  // reload or shared link restores the index the user had selected.
  const [symbol, setSymbol] = useState<AnalystSymbol>(() => {
    const fromUrl = searchParams.get("symbol");
    return isAnalystSymbol(fromUrl) ? fromUrl : "NIFTY";
  });
  // F&O Trade Analysis is scoped to individual F&O-enabled EQUITIES
  // (RELIANCE, TCS, ...), not the NIFTY/BANKNIFTY indices `symbol` above
  // covers -- the backend resolves it against `StockMaster`, which has no
  // row for "NIFTY" (that's an `Instrument`, a different table entirely).
  // Kept as its own state + `?fnoSymbol=` param rather than reusing
  // `symbol`, and uses a free-text stock search (`StockSymbolAutocomplete`)
  // instead of the NIFTY/BANKNIFTY toggle below.
  const [fnoSymbol, setFnoSymbol] = useState<string>(() => searchParams.get("fnoSymbol") ?? "");
  const [indexView, setIndexView] = useState<AnalystIndexView>("both");
  const handleAdjustStrategy = useCallback(() => setTab("strategy-builder"), []);
  const handleViewRecommendation = useCallback(() => setTab("ai-insights"), []);
  // Every tab visited at least once stays mounted from then on (hidden via
  // `TabPanel`'s CSS, not unmounted) — see module docstring.
  const [mountedTabs, setMountedTabs] = useState<Set<AnalystTab>>(() => new Set([tab]));
  useEffect(() => {
    setMountedTabs((prev) => (prev.has(tab) ? prev : new Set(prev).add(tab)));
  }, [tab]);
  // Mirror the active tab into the URL (merge, replace not push) so a
  // bookmark/reload restores the right tab. Merging — rather than
  // overwriting the whole query string — matters here specifically because
  // each child page below independently owns its own `?symbol=` param.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      },
      { replace: true }
    );
  }, [tab, setSearchParams]);
  // Mirror the shared AI Insights / Strategy Builder symbol into the URL
  // the same way, merging rather than overwriting so `?tab=` survives.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("symbol", symbol);
        return next;
      },
      { replace: true }
    );
  }, [symbol, setSearchParams]);
  // Mirror the F&O Trade Analysis stock symbol into `?fnoSymbol=` the same
  // way -- blank clears the param rather than writing an empty value.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        // Only carry the F&O stock in the URL while on its own tab, so it
        // doesn't linger on (e.g.) the Option Indicator URL.
        if (fnoSymbol && tab === "fno-trade-analysis") next.set("fnoSymbol", fnoSymbol);
        else next.delete("fnoSymbol");
        return next;
      },
      { replace: true }
    );
  }, [fnoSymbol, tab, setSearchParams]);

  return (
    <AnalystIndexViewProvider value={indexView}>
    <Box>
      <Box sx={{ position: "sticky", top: 0, zIndex: 11, bgcolor: "background.paper", pt: 0.5 }}>
        <Paper variant="outlined" sx={{ p: 1, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tabs
            value={tab}
            onChange={(_e, value) => setTab(value as AnalystTab)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 36, flexGrow: 1 }}
          >
            {ANALYST_TABS.map((t) => (
              <Tab key={t.value} value={t.value} label={t.label} sx={{ minHeight: 36, py: 0.5 }} />
            ))}
          </Tabs>
          {DUAL_INDEX_TABS.includes(tab) && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={indexView}
              onChange={(_e, value: AnalystIndexView | null) => value && setIndexView(value)}
              aria-label="Index view"
              sx={{
                flexShrink: 0,
                "& .MuiToggleButton-root": {
                  px: { xs: 1, sm: 1.5 },
                  py: 0.25,
                  minHeight: 30,
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  fontWeight: 600,
                  textTransform: "none",
                },
                "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover": {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                },
              }}
            >
              {INDEX_VIEW_OPTIONS.map((o) => (
                <ToggleButton key={o.value} value={o.value} aria-label={`${o.label} view`}>
                  {o.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          <HelpGuideLink section="option-analyst" />
        </Paper>
      </Box>
      {mountedTabs.has("indicator") && (
        <TabPanel active={tab === "indicator"}>
          <OptionIndicatorPage />
        </TabPanel>
      )}
      {mountedTabs.has("charts") && (
        <TabPanel active={tab === "charts"}>
          <OIChartsPage />
        </TabPanel>
      )}
      {mountedTabs.has("strike-charts") && (
        <TabPanel active={tab === "strike-charts"}>
          <OIStrikeChartsPage />
        </TabPanel>
      )}
      {mountedTabs.has("dual") && (
        <TabPanel active={tab === "dual"}>
          <DualSymbolDashboardPage />
        </TabPanel>
      )}
      {(tab === "ai-insights" || tab === "strategy-builder") && (
        <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
          <Tabs
            value={symbol}
            onChange={(_e, value) => isAnalystSymbol(value) && setSymbol(value)}
            sx={{ minHeight: 36 }}
          >
            {SYMBOL_OPTIONS.map((idx) => (
              <Tab key={idx} value={idx} label={idx} sx={{ minHeight: 36, py: 0.5 }} />
            ))}
          </Tabs>
        </Paper>
      )}
      {tab === "fno-trade-analysis" && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <StockSymbolAutocomplete
            value={fnoSymbol}
            onChange={setFnoSymbol}
            label="F&O Stock Symbol"
            helperText="Search any NSE/BSE stock -- Futures & Options data is available once it's onboarded to the OI pipeline"
            sx={{ maxWidth: 360 }}
          />
        </Paper>
      )}
      {mountedTabs.has("ai-insights") && (
        <TabPanel active={tab === "ai-insights"}>
          <StrategyRecommendationSection symbol={symbol} onAdjustStrategy={handleAdjustStrategy} />
          <Box mt={4}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Strategy Insights
            </Typography>
            <StrategyInsightsPanel />
          </Box>
        </TabPanel>
      )}
      {mountedTabs.has("strategy-builder") && (
        <TabPanel active={tab === "strategy-builder"}>
          <StrategyAdjustmentPage symbolProp={symbol} onViewRecommendation={handleViewRecommendation} />
        </TabPanel>
      )}
      {mountedTabs.has("fno-trade-analysis") && (
        <TabPanel active={tab === "fno-trade-analysis"}>
          {fnoSymbol ? (
            <FnoTradeAnalysisPanel symbol={fnoSymbol} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Search for an NSE/BSE stock symbol above to run F&amp;O Trade Analysis.
            </Typography>
          )}
        </TabPanel>
      )}
    </Box>
    </AnalystIndexViewProvider>
  );
}

export default OptionsAnalystPage;
